import { useApolloClient, useMutation } from '@apollo/client';
import { AppRoutePath } from 'config/constants';
import type { ExperimentConfiguration } from 'domains/reporter/Reporter/ExperimentControlTab';
import { experimentConfigurationState } from 'domains/reporter/Reporter/ExperimentControlTab';
import { useAsyncGlobalAction, useGlobalAction } from 'hooks/useGlobalAction';
import Cookie from 'js-cookie';
import analytics from 'modules/analytics';
import { workspace } from 'modules/analytics/constants';
import { removeTypeName } from 'modules/analytics/util';
import {
  AUTH_WITH_EXT_TOKEN,
  AUTHENTICATE,
  AUTHENTICATE_MFA,
  UNAUTHENTICATE,
} from 'modules/Apollo/queries';
import { logger } from 'modules/logger';
import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { atom, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { isRefreshTokenProvided, logout } from 'utils/token';
import { createContext } from '../common/ui/util/createContext';
import {
  AuthWithExtTokenMutation,
  AuthWithExtTokenMutationVariables,
  AuthenticateMutation,
  AuthenticateMutationVariables,
  Role,
  UnauthenticateMutation,
  UnauthenticateMutationVariables,
  User,
} from '../generated/graphql';
import { createPixelDataSharedWorker } from '../modules/viewer/workers/PixelDataSharedWorker';
import { clearStoredData } from './useClearStoredDataOnClose';
import { useSetUserRoles } from './useGetUserRoles';
import { DeepMutable } from 'utils/types';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { useInterval } from 'react-use';

export type Auth = {
  isAuthenticated: boolean;
  error: string | null | undefined; // unknown, LoginComponent is untyped but it only checks if error is not null,
  login: (arg: { email: string; password: string }) => Promise<void>;
  loginMFA: (arg: {
    email: string | null | undefined;
    totp: string | null | undefined;
    mfaSessionId: string | null | undefined;
  }) => Promise<void>;
  authWithExternalToken: (token: string) => Promise<{ caseSmid: string }>;
  logout: () => Promise<void>;
};

export const [AuthProvider, useAuthContext, AuthContext] = createContext<
  Omit<Auth, 'authWithExternalToken'>
>({
  strict: true,
  errorMessage:
    'useAuth: `context` is undefined. Seems you forgot to wrap component within the AuthProvider',
  name: 'AuthContext',
});

export const authState = atom({
  key: 'auth',
  default: isRefreshTokenProvided(),
});

const isAuthedAsExternalUser = atom({
  key: 'ext.auth',
  default: false,
});

export const useIsAuthenticated = (): boolean => useRecoilValue(authState);
export const useIsAuthenticatedAsExternalUser = (): boolean =>
  useRecoilValue(isAuthedAsExternalUser);

/**
 * When a user logs out of one page, the access and refresh token cookies are removed for the origin on all pages.
 * Additionally, a `refresh_token_provided` cookie is set to false, which is what the auth code checks for.
 * Our typical logout flow propagates an event to other pages when a user logs out, which triggers the recoil state updates.
 * However, this event might get lost if the page is hibernating, so this hook will fire and force the unauthenticated state
 * after the page wakes up. Even if the page was hibernating, the cookie changes on one page will still apply to all others
 * in the app.sironamedical.com origin.
 */
export function useMonitorAuthToken() {
  const [isAuthenticated, setAuthenticated] = useRecoilState(authState);

  const checkToken = useCallback(() => {
    const tokenProvided = isRefreshTokenProvided();

    if (tokenProvided !== isAuthenticated) {
      setAuthenticated(tokenProvided);
    }
  }, [isAuthenticated, setAuthenticated]);

  useInterval(checkToken, 1000);
}

/**
 * Identifies a user for analytics after login
 */
function identifyUser(
  authenticateResponse?: User | null,
  experimentConfig?: ExperimentConfiguration
) {
  if (authenticateResponse == null) {
    logger.warn('Empty authentication response when attempting to identify user');
    return;
  }
  const { id, firstName, lastName, email, clinic, reporterSettings } = authenticateResponse;
  const additionalData = {
    segmentData: {
      experimentConfiguration: { ...experimentConfig },
      reporterSettings: removeTypeName(reporterSettings),
    },
  } as const;
  analytics.identify(
    {
      id,
      firstName,
      lastName,
      email,
      clinic: {
        name: clinic?.name || '',
        smid: clinic?.smid || '',
      },
    },
    additionalData
  );

  analytics.track(workspace.usr.login, {
    email,
    clinic: {
      name: clinic?.name ?? '',
      smid: clinic?.smid ?? '',
    },
  });
}

export enum AuthError {
  TOKEN_EXPIRED = 'Token expired',
  TOKEN_INVALID = 'Invalid authentication response',
}

/**
 * Hook for handling all authentication-related operations, which includes: login (with or without
 * MFA), token access, and log out.
 */
const useAuth = (): Auth => {
  const [isAuthenticated, setIsAuthenticated] = useRecoilState(authState);
  const [error, setError] = useState(null);
  const setIsExternalUser = useSetRecoilState(isAuthedAsExternalUser);
  const client = useApolloClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [authenticate] = useMutation<AuthenticateMutation, AuthenticateMutationVariables>(
    AUTHENTICATE
  );
  const [authenticateMFA] = useMutation(AUTHENTICATE_MFA);
  const [unauthenticate] = useMutation<UnauthenticateMutation, UnauthenticateMutationVariables>(
    UNAUTHENTICATE
  );
  const [authWithExtToken] = useMutation<
    AuthWithExtTokenMutation,
    AuthWithExtTokenMutationVariables
  >(AUTH_WITH_EXT_TOKEN, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data?.authWithExtToken?.user?.type === 'ExternalIntegrationUser') {
        setIsExternalUser(true);
      }
    },
    onError: (error) => {
      logger.error('Error authenticating with token', { error });
      throw new Error(AuthError.TOKEN_EXPIRED);
    },
  });
  const experimentConfiguration = useRecoilValue(experimentConfigurationState);
  // treat loading as false
  // do not use the Suspense version of Split, as that will suspend until a user is available
  // this will not always be the case during logout scenarios
  const [useDedupedPixelWorker] = useFeatureFlagEnabled(FF.PIXEL_WORKER_DEDUPLICATION);

  const handleLogout = useCallback(async () => {
    try {
      await unauthenticate({
        // Suppress error when unauthenticating without a valid user, such as when a user's refresh token expires.
        onError: () => {},
      });
    } catch (error) {
      logger.error('Unauthenticate failed:', error);
    }

    try {
      client.stop();

      Cookie.set('refresh_token_provided', 'false');
      analytics.reset();

      const worker = createPixelDataSharedWorker(useDedupedPixelWorker);
      worker.port.postMessage({
        type: 'cancel-transfer',
      });
      await clearStoredData(worker);

      setError(null);
      logout(); // Clears local and session storage of not-persisted entries

      await client.clearStore(); // clear Apollo cache without refetching active queries
    } catch (error) {
      logger.error('Logout and cleanup failed:', error);
    } finally {
      setIsAuthenticated(false);
    }
  }, [client, setIsAuthenticated, unauthenticate, useDedupedPixelWorker]);

  const handleGlobalLogout = useAsyncGlobalAction('use-auth-logout', handleLogout, {
    callSelf: true,
  });

  const handleAuthenticate = useCallback(() => {
    setIsAuthenticated(true);
  }, [setIsAuthenticated]);

  const setIsAuthenticatedGlobally = useGlobalAction('use-auth-login', handleAuthenticate, {
    callSelf: true,
  });

  const setUserRoles = useSetUserRoles();

  /** Callback for common code to execute upon a successful authorization */
  const _postAuthHandler = useCallback(
    async (user: User) => {
      logger.debug('Clearing localforage cache on login');
      const worker = createPixelDataSharedWorker();
      // Clear localforage cache on login so that we don't keep stale data and we allow users to
      // login with a different account and start from scratch
      await clearStoredData(worker);

      // Set the refresh token's expiration
      const expires = new Date();
      expires.setDate(expires.getDate() + 30); // Hardcoded because the token doesn't provide one
      Cookie.set('refresh_token_provided', 'true', { expires });

      // Clear auth error state
      setError(null);
      // Communicate to other windows that the user is authenticated
      setIsAuthenticatedGlobally();
      // Identify the user for analytics
      identifyUser(user, experimentConfiguration);
    },
    [experimentConfiguration, setIsAuthenticatedGlobally]
  );

  const handleLogin = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      setError(null);
      try {
        const response = await authenticate({ variables: { email, password } });

        const authenticateResponse = response.data?.authenticate;

        if (!authenticateResponse) {
          throw new Error(AuthError.TOKEN_INVALID);
        }

        if (authenticateResponse.__typename === 'MFARequired') {
          const mfaSessionId = authenticateResponse?.mfaSessionId;
          navigate(AppRoutePath.VERIFY_MFA, {
            state: {
              mfa_session: mfaSessionId,
              email,
            },
          });
        } else {
          // Extract roles from user object to avoid storing them in GraphQL cache
          // @ts-expect-error [EN-10263] - TS2339 - Property 'roles' does not exist on type '{ readonly __typename?: "MFARequired" | undefined; readonly mfaSessionId: string; } | { readonly __typename?: "User" | undefined; readonly id: string; readonly firstName: string; ... 17 more ...; readonly roles: readonly { ...; }[]; }'.
          const { roles, ...user } = authenticateResponse;

          setUserRoles(roles);
          // TODO: The typecast to user is not ideal here; we should find a way to avoid it. But
          // it's a lesser of two evils to explicitly typecast than to have `user` defined as any
          // due to an untyped useMutation.
          await _postAuthHandler(user as User);
          const from = location.state?.from;
          navigate(from ?? { pathname: AppRoutePath.WORKLIST });
        }
      } catch (err: any) {
        analytics.error(err, { email });
        setError(err);
        return;
      }
    },
    [authenticate, setUserRoles, navigate, location.state, _postAuthHandler]
  );

  const handleMFALogin = useCallback(
    async ({
      email,
      totp,
      mfaSessionId,
    }: {
      email: string | null | undefined;
      totp: string | null | undefined;
      mfaSessionId: string | null | undefined;
    }) => {
      setError(null);
      try {
        const response = await authenticateMFA({
          variables: { email, totp, mfaSessionId },
        });

        if (response.data?.authenticateMFA.roles) {
          setUserRoles(response.data.authenticateMFA.roles);
        }

        await _postAuthHandler(response.data?.authenticateMFA);
        const from = location.state?.from;
        navigate(from ?? { pathname: AppRoutePath.WORKLIST });
      } catch (err: any) {
        analytics.error(err, { email });
        setError(err);
        return;
      }
    },
    [authenticateMFA, _postAuthHandler, location.state, navigate, setUserRoles]
  );

  const handleAuthWithExtToken = useCallback(
    async (token: string) => {
      let data;
      try {
        const resp = await authWithExtToken({ variables: { token } });
        data = resp.data?.authWithExtToken;
      } catch (ex) {
        throw new Error('Invalid authentication response');
      } finally {
        if (!data) {
          throw new Error('Invalid authentication response');
        }
      }
      const { caseSmid, user } = data;
      // @ts-expect-error [EN-10263] - TS18049 - 'user' is possibly 'null' or 'undefined'.
      setUserRoles(user.roles as DeepMutable<Role[]>);
      await _postAuthHandler(user as User);

      return { caseSmid };
    },
    [_postAuthHandler, authWithExtToken, setUserRoles]
  );

  return {
    isAuthenticated,
    logout: handleGlobalLogout,
    login: handleLogin,
    loginMFA: handleMFALogin,
    // @ts-expect-error [EN-10263] - TS2322 - Type '(token: string) => Promise<{ caseSmid: string | null | undefined; }>' is not assignable to type '(token: string) => Promise<{ caseSmid: string; }>'.
    authWithExternalToken: handleAuthWithExtToken,
    error,
  };
};

export default useAuth;
